import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/auth/hooks/useAuth';
import { AUTH_ERROR_MESSAGES } from '../../../shared/auth/constants';
import { AuthLayout, InputField, Checkbox } from '../../../shared/components/modern';
import { ENV } from '../../../config/env';

interface FormErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    agreeToTerms?: string;
}

const CompanySignUp: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        agreeToTerms: false,
    });

    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            const redirectTo = location.state?.from || '/companies/dashboard';
            navigate(redirectTo, { replace: true });
        }
    }, [isAuthenticated, navigate, location]);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (fieldErrors[field as keyof FormErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }

        // Clear general error
        if (error) {
            setError('');
        }
    };

    const validateForm = (): FormErrors => {
        const errors: FormErrors = {};

        if (!formData.firstName.trim()) {
            errors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            errors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            errors.email = AUTH_ERROR_MESSAGES.EMAIL_REQUIRED;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = AUTH_ERROR_MESSAGES.INVALID_EMAIL;
        }

        if (!formData.agreeToTerms) {
            errors.agreeToTerms = AUTH_ERROR_MESSAGES.TERMS_REQUIRED;
        }

        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Call the company signup endpoint (without password)
            const response = await fetch(`${ENV.API_BASE_URL}/auth/v1/signup/company`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    // No password - will be set via email link
                }),
            });

            const data = await response.json();

            if (data.status?.error || !response.ok) {
                throw new Error(data.status?.msg || data.message || 'Signup failed');
            }

            console.log('✅ Company signup successful:', data);

            // Navigate to check email page
            navigate('/auth/company/check-email', {
                state: {
                    email: formData.email,
                    firstName: formData.firstName,
                }
            });

        } catch (err) {
            console.error('🚨 Company signup failed:', err);

            if (err instanceof Error) {
                if (err.message.toLowerCase().includes('already exists')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        email: 'This email address is already registered'
                    }));
                } else {
                    setError(err.message);
                }
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Create Your Company Account"
            subtitle={
                <>
                    Already have an account? <a href="/auth/login" className="text-[#015FA6] font-bold underline decoration-2 underline-offset-4 hover:text-[#043362] transition-colors">Sign in</a>
                </>
            }
            heroVariant="company"
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
                {/* First Name and Last Name - Two Columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <InputField
                        id="firstName"
                        label="First Name"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="First name"
                        error={fieldErrors.firstName}
                        delay={100}
                    />
                    <InputField
                        id="lastName"
                        label="Last Name"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Last name"
                        error={fieldErrors.lastName}
                        delay={150}
                    />
                </div>

                {/* Email Field */}
                <InputField
                    id="email"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="you@company.com"
                    error={fieldErrors.email}
                    delay={200}
                />

                {/* Terms & Conditions */}
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '250ms' }}>
                    <Checkbox
                        checked={formData.agreeToTerms}
                        onChange={(checked) => handleInputChange('agreeToTerms', checked)}
                        error={fieldErrors.agreeToTerms}
                        label={
                            <span className="text-[#015FA6]">
                                I agree to the{' '}
                                <span className="text-[#F59E0B] font-semibold">Terms of Use</span> and{' '}
                                <span className="text-[#F59E0B] font-semibold">Privacy Policy</span>
                            </span>
                        }
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-fade-in-up">
                        <p className="text-sm text-red-600 font-manrope">{error}</p>
                    </div>
                )}

                {/* Sign Up Button */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="
                        w-full h-[60px] mt-4 rounded-xl font-bold text-lg text-white
                        bg-gradient-to-r from-[#015FA6] to-[#0172C6]
                        hover:shadow-[0_8px_25px_-5px_rgba(1,95,166,0.4)]
                        active:scale-[0.98]
                        transition-all duration-300
                        animate-fade-in-up opacity-0
                        flex items-center justify-center gap-2
                        disabled:opacity-70 disabled:cursor-not-allowed
                    "
                    style={{ animationDelay: '300ms' }}
                >
                    {isLoading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Creating Account...
                        </>
                    ) : (
                        'Continue'
                    )}
                </button>

                {/* Info text */}
                <p className="text-center text-sm text-[#015FA6]/60 font-['Manrope'] animate-fade-in-up opacity-0" style={{ animationDelay: '350ms' }}>
                    We'll send you an email to set up your password
                </p>
            </form>
        </AuthLayout>
    );
};

export default CompanySignUp;
